import { useEffect, useState } from 'react';

import { XStack, YStack } from '@/components/ui/layout/layout';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';

import {
  MAX_PREVIEW_COLUMNS,
  MAX_PREVIEW_ROWS,
  readWorkbookSheets,
  type WorkbookSheets,
} from './xlsx-grid';

import type { OfficePreviewProps } from './office-preview.types';

const CELL_CLASS =
  'max-w-64 truncate border border-border px-2 py-1 align-top text-foreground';

export function XlsxPreview({ bytes, onError }: OfficePreviewProps) {
  const [sheets, setSheets] = useState<WorkbookSheets>();
  const [activeSheetIndex, setActiveSheetIndex] = useState(0);

  useEffect(() => {
    let isStale = false;
    void readWorkbookSheets(bytes).then(
      (workbookSheets) => {
        if (isStale) return;
        setSheets(workbookSheets);
        setActiveSheetIndex(0);
      },
      () => {
        if (!isStale) onError();
      }
    );
    return () => {
      isStale = true;
    };
  }, [bytes, onError]);

  if (!sheets)
    return <Skeleton variant="low-emphasis" className="h-full w-full" />;

  const activeSheet = sheets.gridAt(activeSheetIndex);

  return (
    <YStack className="h-full min-h-0 bg-surface">
      {sheets.names.length > 1 && (
        <XStack
          role="tablist"
          aria-label="Worksheets"
          className="shrink-0 gap-1 overflow-x-auto border-b border-border px-2 py-1.5"
        >
          {sheets.names.map((name, index) => (
            <button
              key={name}
              type="button"
              role="tab"
              aria-selected={index === activeSheetIndex}
              onClick={() => setActiveSheetIndex(index)}
              className={cn(
                'shrink-0 rounded-md px-2 py-1 text-xs whitespace-nowrap',
                index === activeSheetIndex
                  ? 'bg-surface-active text-foreground'
                  : 'text-foreground-muted hover:bg-surface-hover'
              )}
            >
              {name}
            </button>
          ))}
        </XStack>
      )}
      <div className="min-h-0 flex-1 overflow-auto select-text">
        {activeSheet.rows.length === 0 ? (
          <span className="block p-4 text-sm text-foreground-muted">
            This sheet is empty.
          </span>
        ) : (
          <table className="w-max border-collapse text-xs">
            <tbody>
              {activeSheet.rows.map((row, rowIndex) => (
                <tr key={rowIndex}>
                  {row.map((cell, columnIndex) => (
                    <td key={columnIndex} className={CELL_CLASS}>
                      {cell}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
      {activeSheet.isTruncated && (
        <span className="shrink-0 border-t border-border px-3 py-1.5 text-xs text-foreground-muted">
          {`Showing the first ${MAX_PREVIEW_ROWS} rows and ${MAX_PREVIEW_COLUMNS} columns. Download the file for the rest.`}
        </span>
      )}
    </YStack>
  );
}
